﻿import React, { ReactNode } from 'react';
import { withRouter } from 'react-router-dom';
import Tabs from '@mui/material/Tabs';
import HomeTab from './tabs/HomeTab';
import AdminOrderTab from './tabs/AdminOrderTab';
import AdminCalendarTab from './tabs/AdminCalendarTab';
import CustomerOrdersTab from './tabs/CustomerOrdersTab';
import CustomerCalendarTab from './tabs/CustomerCalendarTab';
import StatisticsTab from './tabs/StatisticsTab';
import EconomyTab from './tabs/EconomyTab';
import BrokersTab from './tabs/BrokersTab';
import UsersTab from './tabs/UsersTab';
import SettingsTab from './tabs/SettingsTab';
import TabBar from './TabBar';
import * as Features from '../../../../../shared/Features';
import CreateOrderTab from './tabs/CreateOrderTab';
import BrokerAdminTab from './tabs/BrokerAdminTab';
import PhotographersOwnInvoicesTab from './tabs/PhotographersOwnInvoicesTab';
import PhotographerCalendarTab from './tabs/PhotographerCalendarTab';
import PhotographerUploadsTab from './tabs/PhotographerUploadsTab';
import PhotographerOrdersTab from './tabs/PhotographerOrdersTab';
import UneditedOrdersTab from './tabs/UneditedOrdersTab';
import UneditedFutureOrdersTab from './tabs/UneditedFutureOrdersTab';
import PlanEditorOrderListTab from './tabs/PlanEditorOrderListTab';
import StylistCalendarTab from './tabs/StylistCalendarTab';
import StylistAdminCalendarsTab from './tabs/StylistAdminCalendarsTab';
import { useUserContext } from '../../../../../shared/context/user-context';
import { useFeatureFlag } from '../../../../../shared/featureFlags/useFeatureFlag';
import { FeatureFlagKeys } from '../../../../../shared/featureFlags/featureFlagKeys';

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`
    };
}

type IMenuTabObject = {
    name: string;
    routes: string[];
    Component: ReactNode;
    index?: number;
};

type IMenuTab = {
    [key: string]: IMenuTabObject;
};

const MenuTab: IMenuTab = {
    AdminCalendars: {
        name: 'AdminCalendars',
        routes: AdminCalendarTab.routes,
        Component: AdminCalendarTab
    },
    AdminOrders: {
        name: 'AdminOrders',
        routes: AdminOrderTab.routes,
        Component: AdminOrderTab
    },
    BrokerAdmin: {
        name: 'BrokerAdmin',
        routes: BrokerAdminTab.routes,
        Component: BrokerAdminTab
    },
    Customers: {
        name: 'Customers',
        routes: BrokersTab.routes,
        Component: BrokersTab
    },
    CreateOrder: {
        name: 'CreateOrder',
        routes: CreateOrderTab.routes,
        Component: CreateOrderTab
    },
    CustomerOrders: {
        name: 'CustomerOrders',
        routes: CustomerOrdersTab.routes,
        Component: CustomerOrdersTab
    },
    CustomerCalendar: {
        name: 'CustomerCalendar',
        routes: CustomerCalendarTab.routes,
        Component: CustomerCalendarTab
    },
    Economy: {
        name: 'Economy',
        routes: EconomyTab.routes,
        Component: EconomyTab
    },
    Home: {
        name: 'Home',
        routes: HomeTab.routes,
        Component: HomeTab
    },
    PhotographerCalendar: {
        name: 'PhotographerCalendar',
        routes: PhotographerCalendarTab.routes,
        Component: PhotographerCalendarTab
    },
    PhotographerOrders: {
        name: 'PhotographerOrders',
        routes: PhotographerOrdersTab.routes,
        Component: PhotographerOrdersTab
    },
    PhotographerUploads: {
        name: 'PhotographerUploads',
        routes: PhotographerUploadsTab.routes,
        Component: PhotographerUploadsTab
    },
    PhotographersOwnInvoices: {
        name: 'PhotographersOwnInvoices',
        routes: PhotographersOwnInvoicesTab.routes,
        Component: PhotographersOwnInvoicesTab
    },
    PlanEditorOrderList: {
        name: 'PlanEditorOrderList',
        routes: PlanEditorOrderListTab.routes,
        Component: PlanEditorOrderListTab
    },
    Settings: {
        name: 'Settings',
        routes: SettingsTab.routes,
        Component: SettingsTab
    },
    Statistics: {
        name: 'Statistics',
        routes: StatisticsTab.routes,
        Component: StatisticsTab
    },
    StylistCalendar: {
        name: 'StylistCalendar',
        routes: StylistCalendarTab.routes,
        Component: StylistCalendarTab
    },
    StylistAdminCalendars: {
        name: 'StylistAdminCalendars',
        routes: StylistAdminCalendarsTab.routes,
        Component: StylistAdminCalendarsTab
    },
    UneditedOrders: {
        name: 'UneditedOrders',
        routes: UneditedOrdersTab.routes,
        Component: UneditedOrdersTab
    },
    UneditedFutureOrders: {
        name: 'UneditedFutureOrders',
        routes: UneditedFutureOrdersTab.routes,
        Component: UneditedFutureOrdersTab
    },
    Users: {
        name: 'Users',
        routes: UsersTab.routes,
        Component: UsersTab
    }
};

function getCurrentTabByRoute(tabObjects: IMenuTabObject[], path: string): IMenuTabObject {
    return tabObjects.find((menuTabObject) => menuTabObject.routes.includes(path)) || MenuTab.Home;
}

function NavBar() {
    let { user } = useUserContext();

    let shouldShowHomeTab =
        user.isAuthenticated &&
        Features.hasNotFeature(Features.ViewStylingCalendar, user) &&
        Features.hasNotFeature(Features.ViewUneditedOrdersPage, user);
    let shouldShowCreateOrderTab = Features.hasFeature(Features.CreateOrder, user);
    let shouldShowViewOwnPhotographerInvoices = Features.hasFeature(
        Features.ViewOwnPhotographerInvoices,
        user
    );
    let shouldShowCustomerOrders = Features.hasFeature(Features.ListOrders, user);
    let isCustomerCalendarEnabled = useFeatureFlag(FeatureFlagKeys.CustomerCalendar, false);
    let shouldShowCustomerCalendar =
        isCustomerCalendarEnabled && Features.hasFeature(Features.ViewCustomerCalendar, user);
    let shouldShowAdminOrderTab = Features.hasAnyFeature(
        [
            Features.ListPlanOrders,
            Features.ListPhotoOrders,
            Features.ListBookingOrders,
            Features.ListAdvanceSearchOrders
        ],
        user
    );
    let shouldShowViewPhotographerCalendar = Features.hasFeature(Features.ViewCalendar, user);
    let shouldShowViewPhotographerUploads = Features.hasFeature(
        Features.ViewPhotographerOrders,
        user
    );
    let shouldShowAdminCalendarTab = Features.hasFeature(Features.ViewPhotographers, user);
    let shouldShowViewPhotographerOrders = shouldShowViewPhotographerUploads;
    let shouldShowViewUneditedOrdersPage = Features.hasFeature(
        Features.ViewUneditedOrdersPage,
        user
    );
    let shouldShowViewUneditedFutureOrders = Features.hasFeature(
        Features.ViewUneditedFuturePhotoOrderList,
        user
    );
    let shouldShowPlanEditorOrdersList = Features.hasFeature(Features.PlanEditorOrdersList, user);
    let shouldShowStatisticsTab = Features.hasFeature(Features.ViewStatistics, user);
    let shouldShowViewStylingCalendars = Features.hasFeature(Features.ViewStylingCalendars, user);
    let shouldShowViewStylingCalendar =
        !shouldShowViewStylingCalendars && Features.hasFeature(Features.ViewStylingCalendar, user);
    let shouldShowBrokerAdminTab = Features.hasFeature(Features.ListBrokerAgents, user);
    let shouldShowEconomyTab = Features.hasAnyFeature(
        [
            Features.CreateInvoices,
            Features.ViewInvoices,
            Features.ViewAllPhotographerInvoices,
            Features.ListInvoiceJournals,
            Features.ListPaymentJournals,
            Features.CreatePayments,
            Features.ListInvoiceProductType,
            Features.ViewReceivable
        ],
        user
    );
    let shouldShowBrokersTab = Features.hasFeature(Features.ViewBrokerTree, user);
    let shouldShowUsersTab = Features.hasAnyFeature(
        [
            Features.ListSe360Users,
            Features.ViewPhotographers,
            Features.ViewImageEditors,
            Features.ViewImageEditorFirms,
            Features.ListPlanEditors,
            Features.ListPlanFirms,
            Features.ListStylingUsers,
            Features.ListStylingFirms
        ],
        user
    );

    let shouldShowSettingsTab = Features.hasAnyFeature(
        [
            Features.ViewProducts,
            Features.ListProductTypes,
            Features.ViewProductGroups,
            Features.ViewRegions,
            Features.ViewAgreements,
            Features.CreateCustomerAgreements,
            Features.EditCustomerAgreements,
            Features.ListLegalEntity,
            Features.CreatePlanTemplate,
            Features.ListTemplates,
            Features.CreateCommonTexts,
            Features.ViewLogs,
        ],
        user
    );

    let tabObjects: IMenuTabObject[] = [
        shouldShowHomeTab ? MenuTab.Home : null,
        shouldShowCreateOrderTab ? MenuTab.CreateOrder : null,
        shouldShowCustomerOrders ? MenuTab.CustomerOrders : null,
        shouldShowCustomerCalendar ? MenuTab.CustomerCalendar : null,
        shouldShowAdminOrderTab ? MenuTab.AdminOrders : null,
        shouldShowViewPhotographerCalendar ? MenuTab.PhotographerCalendar : null,
        shouldShowViewPhotographerOrders ? MenuTab.PhotographerOrders : null,
        shouldShowViewPhotographerUploads ? MenuTab.PhotographerUploads : null,
        shouldShowAdminCalendarTab ? MenuTab.AdminCalendars : null,
        shouldShowViewOwnPhotographerInvoices ? MenuTab.PhotographersOwnInvoices : null,
        shouldShowViewUneditedOrdersPage ? MenuTab.UneditedOrders : null,
        shouldShowViewUneditedFutureOrders ? MenuTab.UneditedFutureOrders : null,
        shouldShowPlanEditorOrdersList ? MenuTab.PlanEditorOrderList : null,
        shouldShowStatisticsTab ? MenuTab.Statistics : null,
        shouldShowViewStylingCalendar ? MenuTab.StylistCalendar : null,
        shouldShowViewStylingCalendars ? MenuTab.StylistAdminCalendars : null,
        shouldShowBrokerAdminTab ? MenuTab.BrokerAdmin : null,
        shouldShowEconomyTab ? MenuTab.Economy : null,
        shouldShowBrokersTab ? MenuTab.Customers : null,
        shouldShowUsersTab ? MenuTab.Users : null,
        shouldShowSettingsTab ? MenuTab.Settings : null
    ]
        .filter(Boolean)
        .map((tabObject, index) => ({
            ...tabObject,
            index
        }));

    let tabByRoute: IMenuTabObject = getCurrentTabByRoute(tabObjects, window.location.pathname);
    let defaultTab = tabObjects.find((tabObject) => tabObject.name === tabByRoute.name);

    let currentTab = defaultTab ? defaultTab.index : 0;

    return (
        <TabBar>
            <Tabs
                value={currentTab}
                indicatorColor="secondary"
                textColor="inherit"
                aria-label="Main navigation tabs"
                variant="scrollable"
                scrollButtons="auto"
            >
                {tabObjects.map(({ Component, index }) => (
                    <Component key={index} {...a11yProps(index)} />
                ))}
            </Tabs>
        </TabBar>
    );
}

export default withRouter(NavBar);
